import React, { useState, useEffect } from "react";
import {
  heroService,
  HeroSlide,
  imageUploadService,
  MultilingualString,
  getLocalizedString,
} from "../../services/firebaseService";
import "./HeroAdmin.css";

const HeroAdmin: React.FC = () => {
  const [slides, setSlides] = useState<HeroSlide[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingSlide, setEditingSlide] = useState<HeroSlide | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: { en: '', my: '' } as MultilingualString,
    subtitle: { en: '', my: '' } as MultilingualString,
    description: { en: '', my: '' } as MultilingualString,
    cta: {
      products: { en: 'Our Programs', my: '' } as MultilingualString,
      contact: { en: 'Get in Touch', my: '' } as MultilingualString,
    },
    image: "",
    active: true,
    index: 0,
  });
  const [uploading, setUploading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");

  useEffect(() => {
    loadSlides();
  }, []);

  const loadSlides = async () => {
    try {
      setLoading(true);
      const slidesData = await heroService.getAllHeroSlides();
      setSlides(slidesData);
    } catch (error) {
      console.error("Error loading hero slides:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        alert("Please select an image file");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        alert("Image size should be less than 5MB");
        return;
      }

      setImageFile(file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview("");
    setFormData((prev) => ({ ...prev, image: "" }));
  };

  const normalizeMultilingual = (value: string | MultilingualString): MultilingualString => {
    if (typeof value === 'string') {
      return { en: value, my: '' };
    }
    return value;
  };

  const handleOpenForm = (slide?: HeroSlide) => {
    if (slide) {
      setEditingSlide(slide);
      setFormData({
        title: normalizeMultilingual(slide.title),
        subtitle: normalizeMultilingual(slide.subtitle),
        description: slide.description ? normalizeMultilingual(slide.description) : { en: '', my: '' },
        cta: {
          products: normalizeMultilingual(slide.cta.products),
          contact: normalizeMultilingual(slide.cta.contact),
        },
        image: slide.image,
        active: slide.active,
        index: slide.index ?? 0,
      });
      setImagePreview(slide.image);
    } else {
      setEditingSlide(null);
      setFormData({
        title: { en: '', my: '' },
        subtitle: { en: '', my: '' },
        description: { en: '', my: '' },
        cta: {
          products: { en: 'Our Programs', my: '' },
          contact: { en: 'Get in Touch', my: '' },
        },
        image: "",
        active: true,
        index: 0,
      });
      setImagePreview("");
    }
    setImageFile(null);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingSlide(null);
    setFormData({
      title: { en: '', my: '' },
      subtitle: { en: '', my: '' },
      description: { en: '', my: '' },
      cta: {
        products: { en: 'Our Programs', my: '' },
        contact: { en: 'Get in Touch', my: '' },
      },
      image: "",
      active: true,
      index: 0,
    });
    setImageFile(null);
    setImagePreview("");
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;
    
    // Handle multilingual fields (title.en, title.my, subtitle.en, subtitle.my, description.en, description.my)
    if (name.includes('.')) {
      const parts = name.split('.');
      if (parts.length === 2) {
        const [field, lang] = parts;
        if (field === 'title' || field === 'subtitle' || field === 'description') {
          setFormData(prev => ({
            ...prev,
            [field]: {
              ...(prev[field as keyof typeof prev] as MultilingualString),
              [lang]: value
            }
          }));
          return;
        }
      }
      // Handle CTA multilingual fields (cta.products.en, cta.products.my, cta.contact.en, cta.contact.my)
      if (parts.length === 3 && parts[0] === 'cta') {
        const [, ctaField, lang] = parts;
        setFormData(prev => ({
          ...prev,
          cta: {
            ...prev.cta,
            [ctaField]: {
              ...(prev.cta[ctaField as keyof typeof prev.cta] as MultilingualString),
              [lang]: value
            }
          }
        }));
        return;
      }
    }
    
    if (name.startsWith("cta.")) {
      const ctaField = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        cta: {
          ...prev.cta,
          [ctaField]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]:
          type === "checkbox"
            ? (e.target as HTMLInputElement).checked
            : type === "number"
            ? Number(value)
            : value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setUploading(true);

      let imageUrl = formData.image;

      if (imageFile) {
        try {
          imageUrl = await imageUploadService.uploadImage(imageFile, "hero");
        } catch (error) {
          console.error("Error uploading image:", error);
          alert("Failed to upload image. Please try again.");
          setUploading(false);
          return;
        }
      }

      const finalFormData = {
        ...formData,
        image: imageUrl,
      };

      if (editingSlide) {
        await heroService.updateHeroSlide(editingSlide.id!, finalFormData);
      } else {
        await heroService.createHeroSlide(finalFormData);
      }

      handleCloseForm();
      loadSlides();
    } catch (error) {
      console.error("Error saving hero slide:", error);
      alert("Failed to save hero slide. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this hero slide?")) {
      try {
        await heroService.deleteHeroSlide(id);
        loadSlides();
      } catch (error) {
        console.error("Error deleting hero slide:", error);
      }
    }
  };

  const toggleActive = async (slide: HeroSlide) => {
    try {
      await heroService.updateHeroSlide(slide.id!, {
        active: !slide.active,
      });
      loadSlides();
    } catch (error) {
      console.error("Error updating hero slide:", error);
    }
  };

  if (loading) {
    return (
      <div className="hero-admin-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading hero slides...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="hero-admin">
      <div className="hero-admin-header">
        <h2>Hero Slides Management</h2>
        <button className="btn btn-primary" onClick={() => handleOpenForm()}>
          + Add New Slide
        </button>
      </div>

      <div className="hero-slides-grid">
        {slides.map((slide) => (
          <div
            key={slide.id}
            className={`hero-slide-card ${!slide.active ? "inactive" : ""}`}
          >
            <div className="slide-card-image">
              <img src={slide.image} alt={slide.title} />
              {!slide.active && <div className="inactive-badge">Inactive</div>}
            </div>
            <div className="slide-card-content">
              <h3>{getLocalizedString(slide.title, 'en')}</h3>
              <p className="slide-subtitle">{getLocalizedString(slide.subtitle, 'en')}</p>
              <div className="slide-info">
                <span>Index: {slide.index ?? 0}</span>
                <span>Active: {slide.active ? "Yes" : "No"}</span>
              </div>
              <div className="slide-actions">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => handleOpenForm(slide)}
                >
                  Edit
                </button>
                <button
                  className={`btn btn-sm ${
                    slide.active ? "btn-secondary" : "btn-success"
                  }`}
                  onClick={() => toggleActive(slide)}
                >
                  {slide.active ? "Deactivate" : "Activate"}
                </button>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => handleDelete(slide.id!)}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {slides.length === 0 && (
        <div className="empty-state">
          <p>No hero slides found. Create your first slide!</p>
        </div>
      )}

      {isFormOpen && (
        <div className="modal-overlay" onClick={handleCloseForm}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {editingSlide ? "Edit Hero Slide" : "Create New Hero Slide"}
              </h3>
              <button className="modal-close" onClick={handleCloseForm}>
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="hero-form">
              <div className="form-group">
                <label className="form-label">Title *</label>
                <div className="space-y-3">
                  <div>
                    <label className="block mb-1 text-xs text-gray-600">English Title</label>
                    <input
                      type="text"
                      name="title.en"
                      value={formData.title.en}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                      placeholder="Enter title in English"
                    />
                  </div>
                  <div>
                    <label className="block mb-1 text-xs text-gray-600">Myanmar Title (မြန်မာ)</label>
                    <input
                      type="text"
                      name="title.my"
                      value={formData.title.my}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                      placeholder="Enter title in Myanmar"
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Subtitle *</label>
                <div className="space-y-3">
                  <div>
                    <label className="block mb-1 text-xs text-gray-600">English Subtitle</label>
                    <input
                      type="text"
                      name="subtitle.en"
                      value={formData.subtitle.en}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                      placeholder="Enter subtitle in English"
                    />
                  </div>
                  <div>
                    <label className="block mb-1 text-xs text-gray-600">Myanmar Subtitle (မြန်မာ)</label>
                    <input
                      type="text"
                      name="subtitle.my"
                      value={formData.subtitle.my}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                      placeholder="Enter subtitle in Myanmar"
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Description (Optional)</label>
                <div className="space-y-3">
                  <div>
                    <label className="block mb-1 text-xs text-gray-600">English Description</label>
                    <textarea
                      name="description.en"
                      value={formData.description.en}
                      onChange={handleInputChange}
                      className="form-textarea"
                      rows={3}
                      placeholder="Enter description in English"
                    />
                  </div>
                  <div>
                    <label className="block mb-1 text-xs text-gray-600">Myanmar Description (မြန်မာ)</label>
                    <textarea
                      name="description.my"
                      value={formData.description.my}
                      onChange={handleInputChange}
                      className="form-textarea"
                      rows={3}
                      placeholder="Enter description in Myanmar"
                    />
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Products Button Text *</label>
                  <div className="space-y-3">
                    <div>
                      <label className="block mb-1 text-xs text-gray-600">English</label>
                      <input
                        type="text"
                        name="cta.products.en"
                        value={formData.cta.products.en}
                        onChange={handleInputChange}
                        className="form-input"
                        required
                        placeholder="Products button text in English"
                      />
                    </div>
                    <div>
                      <label className="block mb-1 text-xs text-gray-600">Myanmar (မြန်မာ)</label>
                      <input
                        type="text"
                        name="cta.products.my"
                        value={formData.cta.products.my}
                        onChange={handleInputChange}
                        className="form-input"
                        required
                        placeholder="Products button text in Myanmar"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Contact Button Text *</label>
                  <div className="space-y-3">
                    <div>
                      <label className="block mb-1 text-xs text-gray-600">English</label>
                      <input
                        type="text"
                        name="cta.contact.en"
                        value={formData.cta.contact.en}
                        onChange={handleInputChange}
                        className="form-input"
                        required
                        placeholder="Contact button text in English"
                      />
                    </div>
                    <div>
                      <label className="block mb-1 text-xs text-gray-600">Myanmar (မြန်မာ)</label>
                      <input
                        type="text"
                        name="cta.contact.my"
                        value={formData.cta.contact.my}
                        onChange={handleInputChange}
                        className="form-input"
                        required
                        placeholder="Contact button text in Myanmar"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="index" className="form-label">
                  Display Priority (Index)
                </label>
                <input
                  type="number"
                  id="index"
                  name="index"
                  value={formData.index}
                  onChange={handleInputChange}
                  className="form-input"
                  min="0"
                  placeholder="0 = highest priority (appears first)"
                />
                <small
                  style={{
                    display: "block",
                    marginTop: "0.5rem",
                    color: "var(--text-secondary)",
                    fontSize: "0.875rem",
                  }}
                >
                  Lower numbers appear first. Slides with the same index are
                  sorted by creation date.
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="image" className="form-label">
                  Image *
                </label>
                <input
                  type="file"
                  id="image"
                  name="image"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="form-input-file"
                  required={!editingSlide || !formData.image}
                />
                {imagePreview && (
                  <div className="image-preview-container">
                    <div className="image-preview">
                      <img src={imagePreview} alt="Preview" />
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="btn btn-danger btn-sm remove-image-btn"
                        title="Remove image"
                      >
                        ×
                      </button>
                    </div>
                    <p className="image-preview-info">
                      {imageFile
                        ? `Selected: ${imageFile.name}`
                        : "Current image"}
                    </p>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="active"
                    checked={formData.active}
                    onChange={handleInputChange}
                  />
                  <span className="checkmark"></span>
                  Active (Display on website)
                </label>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseForm}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={uploading}
                >
                  {uploading
                    ? "Uploading..."
                    : editingSlide
                    ? "Update Slide"
                    : "Create Slide"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeroAdmin;

